import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { AgentEvent, Cotacao, OfertaAlt, OutgoingPayload, PlanoAlt } from '../types/agent';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export type SSECallbacks = {
  onEvent: (event: AgentEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: Error) => void;
};

/**
 * Evento bruto do nosso backend (gateway_chat/app.py):
 *   {"tipo":"token","texto":...} | {"tipo":"cotacao","dados":{...}} | {"tipo":"done"}
 */
type WireEvent =
  | { tipo: 'token'; texto?: string }
  | { tipo: 'cotacao'; dados?: Record<string, unknown> }
  | { tipo: 'oferta_alt'; dados?: Record<string, unknown> }
  | { tipo: 'done' };

/** Lê a senha única gravada pela tela de senha (Task 2.3). */
function getSenha(): string {
  try {
    return sessionStorage.getItem('senha') ?? '';
  } catch {
    return '';
  }
}

/** Traduz um evento do backend para o formato AgentEvent que o store consome. */
function toAgentEvent(wire: WireEvent): AgentEvent | null {
  switch (wire.tipo) {
    case 'token':
      return { kind: 'text_chunk', text: wire.texto ?? '', done: false };
    case 'cotacao': {
      const dados = wire.dados ?? {};
      const cotacao: Cotacao = {
        preco: typeof dados.preco === 'number' ? (dados.preco as number) : undefined,
        faixa: dados.faixa as Cotacao['faixa'],
        dados,
      };
      return { kind: 'cotacao', cotacao, text: '', done: false };
    }
    case 'oferta_alt': {
      const d = (wire.dados ?? {}) as Record<string, unknown>;
      const planos = Array.isArray(d.planos) ? (d.planos as PlanoAlt[]) : [];
      if (d.tipo !== 'sublingual' || planos.length === 0) return null;
      const oferta: OfertaAlt = {
        tipo: 'sublingual',
        titulo: typeof d.titulo === 'string' ? d.titulo : 'Versão sublingual',
        subtitulo: typeof d.subtitulo === 'string' ? d.subtitulo : 'Liberação prolongada',
        planos,
      };
      return { kind: 'oferta_alt', oferta, text: '', done: false };
    }
    case 'done':
      return { kind: 'done', text: '', done: true };
    default:
      return null;
  }
}

export function openChatStream(
  message: string | OutgoingPayload,
  sessionId: string,
  callbacks: SSECallbacks,
  signal: AbortSignal
): void {
  // Strings are shorthand for a plain text payload; multimodal callers pass the
  // full {tipo, conteudo, mime?} object.
  const payload: OutgoingPayload =
    typeof message === 'string' ? { tipo: 'texto', conteudo: message } : message;
  void fetchEventSource(`${API_BASE}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Senha': getSenha(),
    },
    body: JSON.stringify({
      session_id: sessionId,
      tipo: payload.tipo,
      conteudo: payload.conteudo,
      ...(payload.mime ? { mime: payload.mime } : {}),
    }),
    signal,
    openWhenHidden: true,
    onopen: async (res) => {
      if (!res.ok) throw new Error(`SSE open failed: ${res.status}`);
      callbacks.onOpen?.();
    },
    onmessage: (ev) => {
      if (!ev.data) return;
      try {
        const wire = JSON.parse(ev.data) as WireEvent;
        const agentEvent = toAgentEvent(wire);
        if (agentEvent) callbacks.onEvent(agentEvent);
      } catch {
        // JSON malformado — ignora silenciosamente
      }
    },
    onclose: () => callbacks.onClose?.(),
    onerror: (err) => {
      callbacks.onError?.(err as Error);
      throw err; // interrompe a auto-reconexão do fetch-event-source
    },
  });
}
